import { SUPPORTED_COUNTRIES } from '@klareospace/pharos-lib-types';
import type { SupportedCountry } from '@klareospace/pharos-lib-types';

/**
 * Canonical ISO 3166-1 alpha-3 country code -> full name list.
 * `pharos-lib-utils` is the single source of truth for the supported country list, shared by
 * frontend and backend so they agree on the same codes/names.
 */
export const COUNTRIES = [
  { alpha2: 'AT', code: 'AUT', name: 'Austria' },
  { alpha2: 'BE', code: 'BEL', name: 'Belgium' },
  { alpha2: 'CA', code: 'CAN', name: 'Canada' },
  { alpha2: 'CH', code: 'CHE', name: 'Switzerland' },
  { alpha2: 'DE', code: 'DEU', name: 'Germany' },
  { alpha2: 'DZ', code: 'DZA', name: 'Algeria' },
  { alpha2: 'ES', code: 'ESP', name: 'Spain' },
  { alpha2: 'FR', code: 'FRA', name: 'France' },
  { alpha2: 'GB', code: 'GBR', name: 'United Kingdom' },
  { alpha2: 'IT', code: 'ITA', name: 'Italy' },
  { alpha2: 'LB', code: 'LBN', name: 'Lebanon' },
  { alpha2: 'LU', code: 'LUX', name: 'Luxembourg' },
  { alpha2: 'MA', code: 'MAR', name: 'Morocco' },
  { alpha2: 'NL', code: 'NLD', name: 'Netherlands' },
  { alpha2: 'PT', code: 'PRT', name: 'Portugal' },
  { alpha2: 'TN', code: 'TUN', name: 'Tunisia' },
  { alpha2: 'US', code: 'USA', name: 'United States' },
] as const;

const COUNTRY_CODE_TO_NAME: ReadonlyMap<string, string> = new Map(COUNTRIES.map((c) => [c.code, c.name]));
const COUNTRY_ALPHA2_TO_NAME: ReadonlyMap<string, string> = new Map(COUNTRIES.map((c) => [c.alpha2, c.name]));
const COUNTRY_NAME_TO_NAME: ReadonlyMap<string, string> = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.name]));
const COUNTRY_NAME_TO_CODE: ReadonlyMap<string, string> = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]));
const COUNTRY_CODE_TO_ALPHA2: ReadonlyMap<string, string> = new Map(COUNTRIES.map((c) => [c.code, c.alpha2]));

/** Resolves an alpha-3 code (e.g. "FRA") to its full name. Code matching is case-insensitive. */
export function getCountryNameFromCode(code: string): string | undefined {
  return COUNTRY_CODE_TO_NAME.get(code.toUpperCase());
}

/**
 * Converts a country value to its full name for outgoing queries (e.g. HERE Maps' structured query).
 * Defensive: returns the name when `value` is a known alpha-3 code, otherwise returns `value`
 * unchanged (already a name, or an unrecognized value we should not fail on).
 */
export function toCountryName(value: string): string {
  return getCountryNameFromCode(value) ?? value;
}

/** Normalizes a known alpha-3/alpha-2 code or name (any casing) to the canonical full country name; unknown values pass through. */
export function normalizeCountry(value: string): string {
  if (!value) {
    return value;
  }
  const upper = value.toUpperCase();
  return (
    COUNTRY_CODE_TO_NAME.get(upper) ??
    COUNTRY_ALPHA2_TO_NAME.get(upper) ??
    COUNTRY_NAME_TO_NAME.get(value.toLowerCase()) ??
    value
  );
}

/**
 * Normalizes a country value to its ISO alpha-3 code. Accepts either an already-valid code
 * (e.g. "FRA") or a full name (any casing, e.g. "france") — handles legacy data where country
 * was stored as a name instead of a code. Unknown values pass through unchanged.
 */
export function normalizeCountryCode(value: string | undefined): string {
  if (!value) return '';
  if (COUNTRY_CODE_TO_NAME.has(value)) return value;
  return COUNTRY_NAME_TO_CODE.get(value.toLowerCase()) ?? value;
}

function isSupportedCountry(value: string): value is SupportedCountry {
  return (SUPPORTED_COUNTRIES as readonly string[]).includes(value);
}

/**
 * Resolves a country value (alpha-3 or alpha-2 code, any casing, e.g. "FRA"/"fra"/"FR") to a
 * `SupportedCountry` when it is one of `SUPPORTED_COUNTRIES` (currently France and Lebanon).
 * Returns `undefined` for unsupported, unknown, or empty values.
 */
export function toSupportedCountry(value: string): SupportedCountry | undefined {
  if (!value) return undefined;
  const upper = value.toUpperCase();
  const alpha2 = COUNTRY_CODE_TO_ALPHA2.get(upper) ?? (COUNTRY_ALPHA2_TO_NAME.has(upper) ? upper : undefined);
  return alpha2 && isSupportedCountry(alpha2) ? alpha2 : undefined;
}
